import React, { useMemo, useState } from 'react';

import { Button, DialogContent, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import {
  AlineButton,
  AlineDialog,
  AlineTooltip,
  ConfirmationDialog,
  ConfirmationDialogVariant,
  DocumentPicker,
  DocumentPickerVariant,
  SmallCloseIconButton,
} from 'src/components';
import type { DocumentPickerRow } from 'src/components/generic/DocumentPicker';
import { RelationshipTypeSelect } from 'src/components/generic/RelationshipTypeSelect';
import { HierarchyFilesIcon } from 'src/components/icons/HierarchyFilesIcon';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack, useLogAndDispatchErrorSnack } from 'src/helpers';
import { useBulkCreateRelationships } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  actionsRow: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  bulkAddRelationshipButton: {
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: 0,
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    backgroundColor: 'transparent',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    gap: theme.spacing(1),
    height: 32,
    padding: theme.spacing(1, 1.5),
    textTransform: 'none',
  },
  bulkLeft: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.25),
    marginLeft: theme.spacing(0.5),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
    maxHeight: 'calc(100vh - 64px)',
    maxWidth: 1080,
    minWidth: '75%',
  },
  content: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
    minHeight: 0,
    overflowY: 'hidden',
    width: '100%',
  },
  documentPicker: {
    '&&': {
      maxHeight: 'none',
      minHeight: 0,
    },
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    fontWeight: 400,
  },
  subtitleDocName: {
    fontWeight: 700,
  },
}));

export type RelatedDocumentsDirection = 'parent' | 'child';

const PARENT_RELATIONSHIP_LABELS = {
  [Apollo.ChildRelationshipType.Amendment]: 'Amended by',
  [Apollo.ChildRelationshipType.Appendix]: 'Has an appendix',
  [Apollo.ChildRelationshipType.Assignment]: 'Assigned by',
  [Apollo.ChildRelationshipType.Correction]: 'Corrected by',
  [Apollo.ChildRelationshipType.Exhibit]: 'Exhibit attached',
  [Apollo.ChildRelationshipType.Extension]: 'Extended by',
  [Apollo.ChildRelationshipType.Flowdown]: 'Flowed down to',
  [Apollo.ChildRelationshipType.GovernedBy]: 'Governs',
  [Apollo.ChildRelationshipType.Incorporation]: 'Incorporated by',
  [Apollo.ChildRelationshipType.Mention]: 'Mentioned by',
  [Apollo.ChildRelationshipType.Novation]: 'Novated by',
  [Apollo.ChildRelationshipType.Related]: 'Related to',
  [Apollo.ChildRelationshipType.Renewal]: 'Renewed by',
  [Apollo.ChildRelationshipType.Schedule]: 'Schedule attached',
  [Apollo.ChildRelationshipType.Supplement]: 'Supplemented by',
  [Apollo.ChildRelationshipType.Supersede]: 'Superseded by',
  [Apollo.ChildRelationshipType.Termination]: 'Terminated by',
} satisfies Record<Apollo.ChildRelationshipType, string>;

const CHILD_RELATIONSHIP_LABELS = {
  [Apollo.ChildRelationshipType.Amendment]: 'Amends',
  [Apollo.ChildRelationshipType.Appendix]: 'Appendix to',
  [Apollo.ChildRelationshipType.Assignment]: 'Assigns',
  [Apollo.ChildRelationshipType.Correction]: 'Corrects',
  [Apollo.ChildRelationshipType.Exhibit]: 'Exhibit to',
  [Apollo.ChildRelationshipType.Extension]: 'Extends',
  [Apollo.ChildRelationshipType.Flowdown]: 'Flows down from',
  [Apollo.ChildRelationshipType.GovernedBy]: 'Governed by',
  [Apollo.ChildRelationshipType.Incorporation]: 'Incorporates',
  [Apollo.ChildRelationshipType.Mention]: 'Mentioned by',
  [Apollo.ChildRelationshipType.Novation]: 'Novates',
  [Apollo.ChildRelationshipType.Related]: 'Related to',
  [Apollo.ChildRelationshipType.Renewal]: 'Renews',
  [Apollo.ChildRelationshipType.Schedule]: 'Schedule to',
  [Apollo.ChildRelationshipType.Supplement]: 'Supplements',
  [Apollo.ChildRelationshipType.Supersede]: 'Supersedes',
  [Apollo.ChildRelationshipType.Termination]: 'Terminates',
} satisfies Record<Apollo.ChildRelationshipType, string>;

const RELATIONSHIP_LABELS_BY_DIRECTION = {
  child: CHILD_RELATIONSHIP_LABELS,
  parent: PARENT_RELATIONSHIP_LABELS,
} satisfies Record<RelatedDocumentsDirection, Record<Apollo.ChildRelationshipType, string>>;

export function getRelationshipLabel(args: {
  direction: RelatedDocumentsDirection;
  type: Apollo.ChildRelationshipType;
}) {
  return RELATIONSHIP_LABELS_BY_DIRECTION[args.direction][args.type];
}

export interface SelectRelatedDocumentsWithRelationshipDialogProps {
  direction: RelatedDocumentsDirection;
  /** The document you are linking others to (may also be selected to create a self-referencing relationship). */
  documentId: UUID;
  onClose: () => void;
  open: boolean;
  repositoryId: UUID;
  /** Optional hook after a successful save (e.g. clear grid selection). */
  afterRelationshipsSaved?: () => void;
  sourceDocumentName?: string;
}

export function SelectRelatedDocumentsWithRelationshipDialog({
  afterRelationshipsSaved,
  direction,
  documentId,
  onClose,
  open,
  repositoryId,
  sourceDocumentName,
}: SelectRelatedDocumentsWithRelationshipDialogProps) {
  const { classes } = useStyles();
  const [bulkCreateDocumentRelationships] = useBulkCreateRelationships(documentId);
  const dispatchRelationshipsSavedSnack = useDispatchSuccessSnack({
    message: 'Relationship(s) saved.',
  });
  const logAndDispatchRelationshipsError = useLogAndDispatchErrorSnack({
    message: 'Some relationships could not be saved. Please try again.',
  });
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<UUID[]>([]);
  const [relationshipByDocId, setRelationshipByDocId] = useState<
    Record<string, Apollo.ChildRelationshipType | null>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [bulkRelationshipSelectAnchorEl, setBulkRelationshipSelectAnchorEl] =
    useState<HTMLElement | null>(null);
  const [bulkRelationshipSelectOpen, setBulkRelationshipSelectOpen] = useState(false);
  const rowsWithRelationshipCount = Object.values(relationshipByDocId).filter(Boolean).length;
  const doneButtonDisabledReason = !rowsWithRelationshipCount
    ? 'Set a relationship for at least one document'
    : '';

  function closeBulkRelationshipSelect() {
    setBulkRelationshipSelectAnchorEl(null);
    setBulkRelationshipSelectOpen(false);
  }

  function resetSelectionState() {
    setSelectedDocumentIds([]);
    setRelationshipByDocId({});
    closeBulkRelationshipSelect();
  }

  function clearSelectionOnly() {
    setSelectedDocumentIds([]);
    setRelationshipByDocId({});
    closeBulkRelationshipSelect();
  }

  function handleClose() {
    resetSelectionState();
    setSubmitting(false);
    onClose();
  }

  async function handleDone() {
    if (!rowsWithRelationshipCount) return;

    const items = Object.entries(relationshipByDocId).flatMap(
      ([relatedDocumentId, relationshipType]) => {
        if (!relationshipType) return [];
        return [
          {
            childDocumentId: direction === 'parent' ? documentId : relatedDocumentId,
            childRelationshipType: relationshipType,
            parentDocumentId: direction === 'parent' ? relatedDocumentId : documentId,
          },
        ];
      }
    );
    if (!items.length) return;

    try {
      setSubmitting(true);
      const { data } = await bulkCreateDocumentRelationships({
        variables: { items },
      });
      if (data?.bulkCreateDocumentRelationships?.length) dispatchRelationshipsSavedSnack();
      afterRelationshipsSaved?.();
      handleClose();
    } catch (err) {
      logAndDispatchRelationshipsError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSubmitting(false);
    }
  }

  const relationshipCol = useMemo((): GridColDef<DocumentPickerRow> => {
    return {
      field: 'relationship',
      flex: 1,
      headerName: 'Relationship',
      renderCell: (params: GridRenderCellParams<DocumentPickerRow>) => {
        const docId = params.row.id;
        const value = relationshipByDocId[docId] ?? '';

        if (!value) {
          return (
            <Typography
              color="textSecondary"
              sx={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 400 }}
              variant="body2"
            >
              Not set
            </Typography>
          );
        }

        return (
          <Typography sx={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 700 }} variant="body2">
            {getRelationshipLabel({ direction, type: value })}
          </Typography>
        );
      },
      sortable: false,
    };
  }, [direction, relationshipByDocId]);

  return (
    <AlineDialog
      classes={{ paperWidthMd: classes.container }}
      contentPaddingBottomPx={0}
      contentPaddingTopPx={8}
      open={open}
      title="Select Related Documents"
      onClose={handleClose}
    >
      <DialogContent className={classes.content}>
        {sourceDocumentName && (
          <Typography className={classes.subtitle} variant="body2">
            {direction === 'child'
              ? `You're adding child documents to `
              : `You're adding parent documents to `}
            <span className={classes.subtitleDocName}>{`"${sourceDocumentName}"`}</span>
          </Typography>
        )}
        <DocumentPicker
          showFiltersButton
          className={classes.documentPicker}
          extraColumns={[relationshipCol]}
          extraColumnsAfterField="parties"
          extraToolbarContentLeft={
            Boolean(rowsWithRelationshipCount) && (
              <div className={classes.bulkLeft}>
                <Typography
                  sx={{ fontFamily: 'inherit', fontSize: 14, fontWeight: 700 }}
                  variant="body2"
                >
                  {`Setting ${rowsWithRelationshipCount} relationship${rowsWithRelationshipCount === 1 ? '' : 's'}`}
                </Typography>
                <AlineTooltip arrow placement="top" title="Clear relationships">
                  <SmallCloseIconButton
                    disabled={submitting}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setClearConfirmOpen(true);
                    }}
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                </AlineTooltip>
              </div>
            )
          }
          extraToolbarContentRight={
            Boolean(selectedDocumentIds.length) && (
              <>
                <Button
                  ref={setBulkRelationshipSelectAnchorEl}
                  className={classes.bulkAddRelationshipButton}
                  disabled={submitting}
                  startIcon={<HierarchyFilesIcon height={16} stroke="currentColor" width={16} />}
                  variant="outlined"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!selectedDocumentIds.length || submitting) return;
                    setBulkRelationshipSelectOpen(true);
                  }}
                  onMouseDown={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  Set relationship
                </Button>
                <RelationshipTypeSelect
                  anchorEl={bulkRelationshipSelectAnchorEl}
                  getLabel={type => getRelationshipLabel({ direction, type })}
                  menuTitle={`Select the type of ${direction} relation`}
                  open={bulkRelationshipSelectOpen}
                  value=""
                  onChange={event => {
                    const type = event.target.value;
                    if (!type) return;
                    setRelationshipByDocId(prev => {
                      const next = { ...prev };
                      selectedDocumentIds.forEach(id => {
                        next[id] = type;
                      });
                      return next;
                    });
                    setBulkRelationshipSelectOpen(false);
                  }}
                  onClose={() => setBulkRelationshipSelectOpen(false)}
                />
              </>
            )
          }
          pickerVariant={DocumentPickerVariant.MultiSelect}
          repositoryId={repositoryId}
          searchPlaceholder="Search documents..."
          selectedDocumentIds={selectedDocumentIds}
          onSelectionChange={ids => {
            setSelectedDocumentIds(ids);
            if (!ids.length) closeBulkRelationshipSelect();
            setRelationshipByDocId(prev => {
              const next: Record<string, Apollo.ChildRelationshipType | null> = { ...prev };
              ids.forEach(id => {
                if (!(id in next)) next[id] = null;
              });

              return next;
            });
          }}
        />

        <div className={classes.actionsRow}>
          <div style={{ flex: 1 }} />
          <AlineTooltip arrow placement="top" title={doneButtonDisabledReason}>
            <span>
              <AlineButton
                color="primary"
                disabled={!rowsWithRelationshipCount}
                loading={submitting}
                variant="contained"
                onClick={handleDone}
              >
                Set Relationships
              </AlineButton>
            </span>
          </AlineTooltip>
        </div>
      </DialogContent>
      <ConfirmationDialog
        message={`This will remove ${rowsWithRelationshipCount} relationship${rowsWithRelationshipCount === 1 ? '' : 's'} set.`}
        open={clearConfirmOpen}
        primaryText="Clear"
        title="Clear relationships?"
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={() => {
          clearSelectionOnly();
          setClearConfirmOpen(false);
        }}
        onClose={() => setClearConfirmOpen(false)}
      />
    </AlineDialog>
  );
}
